/**
 * Beroepsregistratiecertificaat (Professional Compliance Ledger) in de cockpit van de professional.
 * Preview van de drie pijlers (PE-punten, Teams-geverifieerde intervisie, Stripe-betaalde praktijkuren)
 * en export als PDF/CSV met SHA-256-zegel; auditors verifiëren via /verify/certificaat/:hash.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Award, Calendar, ChevronDown, ChevronUp, Clock, Copy, Download, ExternalLink, FileSpreadsheet, Loader2, Users } from 'lucide-react';
import { CertificaatFormaat, downloadCertificaat, foutmelding, haalLedger, verificatieUrl } from '../services/beroepsregistratieApi';
import { RegelsTabel } from './BeroepsregistratieRegels';
import { fmtGetal } from '../utils/beroepsregistratieFormat';

/** Lokale YYYY-MM-DD (geen UTC-verschuiving rond middernacht). */
const isoDag = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const laatste12Maanden = () => {
  const tot = new Date();
  const van = new Date(tot);
  van.setFullYear(van.getFullYear() - 1);
  van.setDate(van.getDate() + 1);
  return { van: isoDag(van), tot: isoDag(tot) };
};
const jaarPeriode = (jaar: number) => ({ van: `${jaar}-01-01`, tot: `${jaar}-12-31` });

const Tegel: React.FC<{ icon: React.ReactNode; label: string; waarde: string; bron: string; children?: React.ReactNode }> = ({ icon, label, waarde, bron, children }) => (
  <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
    <div className="flex items-center gap-2 text-[#0B1F3A]">
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 text-2xl font-bold text-[#0B1F3A]">{waarde}</p>
    <p className="mt-1 text-xs text-gray-500">{bron}</p>
    {children}
  </div>
);

export const BeroepsregistratieCertificaatKaart: React.FC = () => {
  const { t } = useTranslation('pages');
  const standaard = useMemo(laatste12Maanden, []);
  const [periode, setPeriode] = useState(standaard);
  const [toonRegels, setToonRegels] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [uitgifte, setUitgifte] = useState<{ hash: string; formaat: CertificaatFormaat } | null>(null);
  const [gekopieerd, setGekopieerd] = useState(false);
  const huidigJaar = new Date().getFullYear();

  const geldig = /^\d{4}-\d{2}-\d{2}$/.test(periode.van) && /^\d{4}-\d{2}-\d{2}$/.test(periode.tot) && periode.van <= periode.tot;

  const ledger = useQuery({
    queryKey: ['compliance-ledger', periode.van, periode.tot],
    queryFn: () => haalLedger(periode.van, periode.tot),
    enabled: geldig,
    staleTime: 60_000,
  });

  const download = useMutation({
    mutationFn: (formaat: CertificaatFormaat) => downloadCertificaat(periode.van, periode.tot, formaat),
    onMutate: () => {
      setFout(null);
      setGekopieerd(false);
    },
    onSuccess: (r, formaat) => setUitgifte(r.hash ? { hash: r.hash, formaat } : null),
    onError: async (e) => setFout(await foutmelding(e, t('beroepsregistratie.downloadError'))),
  });

  const kopieer = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setGekopieerd(true);
      setTimeout(() => setGekopieerd(false), 2000);
    } catch {
      window.prompt(t('beroepsregistratie.copyPrompt'), url);
    }
  };

  const zetPeriode = (p: { van: string; tot: string }) => {
    setPeriode(p);
    setUitgifte(null);
  };

  const l = ledger.data;
  const isStandaard = periode.van === standaard.van && periode.tot === standaard.tot;

  return (
    <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" aria-labelledby="beroepsregistratie-titel">
      <div className="flex items-start gap-4">
        <img src="/keurmerk-beroepsregistratie.png" alt={t('beroepsregistratie.sealAlt')} className="h-28 w-auto flex-shrink-0 object-contain" width={69} height={112} loading="lazy" />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#E5A425]">{t('beroepsregistratie.eyebrow')}</p>
          <h3 id="beroepsregistratie-titel" className="text-base font-bold text-gray-800">{t('beroepsregistratie.title')}</h3>
          <p className="mt-1 text-xs text-gray-500 leading-relaxed">{t('beroepsregistratie.subtitle')}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Calendar size={15} className="text-[#0B1F3A]" />
          {t('beroepsregistratie.period')}
        </div>
        <label className="text-xs text-gray-500">
          {t('beroepsregistratie.from')}
          <input
            type="date"
            value={periode.van}
            max={periode.tot}
            onChange={(e) => zetPeriode({ ...periode, van: e.target.value })}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800"
          />
        </label>
        <label className="text-xs text-gray-500">
          {t('beroepsregistratie.to')}
          <input
            type="date"
            value={periode.tot}
            min={periode.van}
            onChange={(e) => zetPeriode({ ...periode, tot: e.target.value })}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800"
          />
        </label>
        <button
          type="button"
          onClick={() => zetPeriode(laatste12Maanden())}
          aria-pressed={isStandaard}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
            isStandaard ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-200 text-gray-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A]'
          }`}
        >
          {t('beroepsregistratie.last12Months')}
        </button>
        {[huidigJaar, huidigJaar - 1].map((jaar) => (
          <button
            key={jaar}
            type="button"
            onClick={() => zetPeriode(jaarPeriode(jaar))}
            className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A]"
          >
            {jaar}
          </button>
        ))}
      </div>

      {!geldig && <p className="mt-3 text-xs text-red-600">{t('beroepsregistratie.invalidPeriod')}</p>}

      {ledger.isLoading && geldig ? (
        <div className="mt-5 grid gap-3 sm:grid-cols-3 animate-pulse" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-lg bg-gray-100" />
          ))}
        </div>
      ) : ledger.isError ? (
        <p className="mt-5 text-xs text-red-600" role="alert">{t('beroepsregistratie.loadError')}</p>
      ) : l ? (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Tegel
              icon={<Award size={15} />}
              label={t('beroepsregistratie.peLabel')}
              waarde={t('beroepsregistratie.points', { n: fmtGetal(l.totalen.pe_punten) })}
              bron={t('beroepsregistratie.peSource')}
            >
              {l.totalen.per_register.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                  {l.totalen.per_register.map((r) => (
                    <li key={r.register}>
                      {r.register}: <span className="font-semibold">{fmtGetal(r.punten)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Tegel>
            <Tegel
              icon={<Users size={15} />}
              label={t('beroepsregistratie.intervisionLabel')}
              waarde={t('beroepsregistratie.hours', { n: fmtGetal(l.totalen.intervisie_uren) })}
              bron={t('beroepsregistratie.intervisionSource', { count: l.totalen.intervisie_sessies })}
            />
            <Tegel
              icon={<Clock size={15} />}
              label={t('beroepsregistratie.practiceLabel')}
              waarde={t('beroepsregistratie.hours', { n: fmtGetal(l.totalen.praktijk_uren) })}
              bron={t('beroepsregistratie.practiceSource', { count: l.totalen.praktijk_facturen })}
            />
          </div>

          {l.afgewezen_intervisies > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              <span>{t('beroepsregistratie.rejectedIntervision', { count: l.afgewezen_intervisies })}</span>
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={() => setToonRegels((v) => !v)}
              aria-expanded={toonRegels}
              aria-controls="beroepsregistratie-regels"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#0B1F3A] hover:underline"
            >
              {toonRegels ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              {t('beroepsregistratie.evidenceToggle', { count: l.regels.length })}
            </button>
            {toonRegels && (
              <div id="beroepsregistratie-regels" className="mt-2">
                {l.regels.length === 0 ? (
                  <p className="text-xs text-gray-500">{t('beroepsregistratie.noRows')}</p>
                ) : (
                  <div className="max-h-80 overflow-y-auto">
                    <RegelsTabel regels={l.regels} compact />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : null}

      <div className="mt-5 rounded-lg border border-dashed border-[#E5A425]/60 bg-[#E5A425]/5 p-4">
        <p className="text-sm font-semibold text-gray-700">{t('beroepsregistratie.exportTitle')}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!geldig || download.isPending}
            onClick={() => download.mutate('pdf')}
            className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {download.isPending && download.variables === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {t('beroepsregistratie.downloadPdf')}
          </button>
          <button
            type="button"
            disabled={!geldig || download.isPending}
            onClick={() => download.mutate('csv')}
            className="inline-flex items-center gap-2 rounded-lg border border-[#0B1F3A] px-5 py-2.5 text-sm font-semibold text-[#0B1F3A] hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            {download.isPending && download.variables === 'csv' ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
            {t('beroepsregistratie.downloadCsv')}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">{t('beroepsregistratie.exportHint')}</p>
        {fout && <p className="mt-2 text-xs text-red-600" role="alert">{fout}</p>}
        {uitgifte && (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-800" role="status">
            <p className="font-semibold">{t('beroepsregistratie.issued', { format: uitgifte.formaat.toUpperCase() })}</p>
            <p className="mt-1 break-all font-mono text-[10px] text-green-900">SHA-256: {uitgifte.hash}</p>
            <div className="mt-2 flex flex-wrap gap-3">
              <a
                href={verificatieUrl(uitgifte.hash)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-[#0B1F3A] hover:underline"
              >
                <ExternalLink size={13} /> {t('beroepsregistratie.openVerification')}
              </a>
              <button type="button" onClick={() => kopieer(verificatieUrl(uitgifte.hash))} className="inline-flex items-center gap-1 font-semibold text-[#0B1F3A] hover:underline">
                <Copy size={13} /> {gekopieerd ? t('beroepsregistratie.copied') : t('beroepsregistratie.copyLink')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BeroepsregistratieCertificaatKaart;
